import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { DEAL_STAGE_VALUES } from "@/lib/constants/deal-stages";
import type { DealStage } from "@/lib/types/database/deals";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  lead: "Lead",
  qualified: "Qualified",
  discovery: "Discovery",
  proposal: "Proposal",
  negotiation: "Negotiation",
  contract_signed: "Contract Signed",
  deal_won: "Deal Won",
  hand_off: "Hand Off",
  started: "Started",
  in_progress: "In Progress",
  completed: "Completed",
  invoiced: "Invoiced",
  paid: "Paid",
  sm_lost_no_savings: "SM Lost — No Savings Found",
  sm_lost_savings_customer_rejected: "SM Lost — Savings Found, Customer Rejected",
  wtc_lost_solution_rejected: "WTC Lost — Solution Rejected",
  wtc_lost_over_budget: "WTC Lost — Over Budget",
  wtc_lost_no_budget: "WTC Lost — No Budget",
};

const DEAL_STAGE_OPTIONS = DEAL_STAGE_VALUES.map((value) => ({
  value,
  label: DEAL_STAGE_LABELS[value],
}));

/**
 * Saved view ids that include every deal field by default — used as the
 * `savedViewMembership` value for fields that appear in all views (title,
 * company_id, revenue, last_communication_at).
 *
 * Kept in this file (rather than `_helpers.ts`) so each field declaration
 * stays self-contained when read in isolation.
 */
const ALL_DEAL_VIEW_IDS = [
  "all-deals",
  "sales-pipeline",
  "not-followed-up-this-week",
  "scheduled-followups",
  "in-progress",
  "closed-deals",
  "overdue-deals",
  "unassigned-deals",
  "at-risk",
  "won-deals",
  "deals-ready-to-invoice",
  "deals-invoiced",
  "recurring-revenue",
  "committed-revenue",
  "my-deals",
] as const;

export const coreDealsFields = defineFieldConfigMap({
  // FIELD: title
  title: {
    type: "text",
    label: "Deal Title",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: false,
    editable: true,
    bulkEditable: true,
    formSpan: 3,
    filterType: "contains",
    width: 280,
    minWidth: 200,
    maxWidth: 400,
    required: true,
    validation: {
      required: "Deal title is required.",
    },
    placeholder: "Enter deal title",
    icon: "Briefcase",
    // Role-based access control - visible to all roles except client_user
    allowedRoles: [...ALL_STAFF_ROLES],
    // Show row actions inline with this column
    showRowActions: true,
    showOnCard: true,
    /** Header row: share row with stage on wide cards (2+1 in a 3-col grid). */
    cardSpan: 2,
    mobileDetailSectionId: "overview",
    savedViewMembership: ALL_DEAL_VIEW_IDS,
  },
  // FIELD: revenue
  revenue: {
    type: "number",
    label: "Revenue",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: false,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "range",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    required: true,
    validation: {
      required: "Revenue is required.",
      min: 0,
    },
    placeholder: "0.00",
    icon: "DollarSign",
    /** Renders as `$25,000` in tables, cards, and the pipeline board. */
    numberFormat: "currency",
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "overview",
    savedViewMembership: ALL_DEAL_VIEW_IDS,
  },
  // FIELD: currency
  currency: {
    type: "text",
    label: "Currency",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    formSpan: 1,
    width: 100,
    minWidth: 80,
    maxWidth: 120,
    placeholder: "USD",
    formOnly: true,
    allowedRoles: [...ALL_STAFF_ROLES],
    mobileDetailSectionId: "overview",
  },
  // FIELD: stage
  stage: {
    type: "select",
    label: "Stage",
    isStatusField: true,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: false,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "exact",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    groupable: true,
    options: DEAL_STAGE_OPTIONS,
    required: true,
    validation: {
      required: "Stage is required.",
    },
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "overview",
    savedViewMembership: [
      "all-deals",
      "sales-pipeline",
      "not-followed-up-this-week",
      "scheduled-followups",
      "in-progress",
      "closed-deals",
      "overdue-deals",
      "unassigned-deals",
      "deals-ready-to-invoice",
      "deals-invoiced",
      "my-deals",
    ],
  },
  // FIELD: close_probability
  close_probability: {
    type: "number",
    label: "Probability %",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 1,
    filterType: "range",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    validation: {
      min: 0,
      max: 100,
    },
    placeholder: "0-100",
    icon: "Percent",
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "overview",
    savedViewMembership: [
      "all-deals",
      "sales-pipeline",
      "not-followed-up-this-week",
      "unassigned-deals",
      "at-risk",
      "my-deals",
    ],
  },
  // FIELD: close_date
  close_date: {
    type: "date",
    label: "Close Date",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    bulkEditable: true,
    formSpan: 2,
    filterType: "dateRange",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "Calendar",
    groupable: true,
    datetimePresets: {
      enabled: true,
      showCustomRange: true,
      presets: [
        "today",
        "tomorrow",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "last60days",
        "last90days",
        "thisweek",
        "lastweek",
        "nextweek",
        "thismonth",
        "lastmonth",
        "nextmonth",
        "thisquarter",
        "lastquarter",
        "nextquarter",
        "thisyear",
        "lastyear",
        "nextyear",
      ],
    },
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "overview",
    savedViewMembership: [
      "all-deals",
      "sales-pipeline",
      "not-followed-up-this-week",
      "in-progress",
      "overdue-deals",
      "unassigned-deals",
      "at-risk",
      "recurring-revenue",
      "committed-revenue",
      "my-deals",
    ],
  },
});
